import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectIsStoreAuthV4UseLmdCoreEnabled } from '@/ducks/app/selectors/app.selectors'
import {
  selectAuthSource,
  selectLoginAs,
  selectPermissions,
  selectRolesLoaded
} from '@/ducks/auth/auth.selectors'
import { Permission, PermissionRequirement } from '@/ducks/auth/auth.types'

export const makePermissionKey = ({
  resource,
  operation
}: PermissionRequirement): string => `${resource}:${operation}`

/** Baseline staff UI when RBAC returns no roles/permissions (Orders + Menu view/hide + Settings). */
export const isDefaultStaffMenuPermission = (
  resource: string,
  operation: string
): boolean =>
  resource === 'menu' && (operation === 'view' || operation === 'hide')

export const shouldUseStaffMenuDefaults = (
  isRbacEnabled: boolean,
  permissions: Permission[]
): boolean => isRbacEnabled && permissions.length === 0

export const hasPermission = (
  permissions: Permission[] = [],
  requirement?: PermissionRequirement,
  options?: { isRbacEnabled?: boolean }
): boolean => {
  if (!requirement) return true

  const isRbacEnabled = Boolean(options?.isRbacEnabled)
  if (
    shouldUseStaffMenuDefaults(isRbacEnabled, permissions) &&
    isDefaultStaffMenuPermission(requirement.resource, requirement.operation)
  ) {
    return true
  }

  return permissions.some(
    permission =>
      permission.resource === requirement.resource &&
      permission.operation === requirement.operation
  )
}

export const useAccessControl = () => {
  const isFlagEnabled = useSelector(selectIsStoreAuthV4UseLmdCoreEnabled)
  const authSource = useSelector(selectAuthSource)
  const loginAs = useSelector(selectLoginAs)
  const baseRbac = isFlagEnabled || authSource === 'lmd-core'
  // Main store owner: full UI without permission keys from /me/roles
  const isRbacEnabled = baseRbac && loginAs !== 'owner'
  const permissionsFromStore = useSelector(selectPermissions)
  const permissions = useMemo(
    () => (Array.isArray(permissionsFromStore) ? permissionsFromStore : []),
    [permissionsFromStore]
  )
  const rolesLoaded = useSelector(selectRolesLoaded)

  const permissionSet = useMemo(
    () => new Set(permissions.map(makePermissionKey)),
    [permissions]
  )

  const staffMenuDefaults = shouldUseStaffMenuDefaults(
    isRbacEnabled,
    permissions
  )

  const can = (resource: string, operation: string): boolean => {
    if (!isRbacEnabled) return true
    if (
      staffMenuDefaults &&
      isDefaultStaffMenuPermission(resource, operation)
    ) {
      return true
    }
    return permissionSet.has(makePermissionKey({ resource, operation }))
  }

  return {
    isRbacEnabled,
    permissions,
    can,
    canViewPerformance: can('performance', 'view'),
    canViewBilling: can('billing', 'view'),
    canViewMenu: can('menu', 'view'),
    canHideMenuItems: can('menu', 'hide'),
    canEditMenuItems: can('menu', 'edit'),
    canManageOffers: can('offers', 'manage'),
    canViewBusinessHours: can('business_hours', 'view'),
    canEditBusinessHours: can('business_hours', 'edit'),
    rolesLoaded: !isRbacEnabled || rolesLoaded
  }
}
